import { useState, useEffect } from 'react';
import Layout from '../../components/feature/Layout';
import { useReveal } from '../../hooks/useReveal';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import type { AboutSection } from '../../lib/supabase';

type Lang = 'ko' | 'en' | 'vi';

export default function AboutPage() {
  const ref = useReveal();
  const { i18n, t } = useTranslation();
  const [aboutData, setAboutData] = useState<Record<string, Record<string, Record<string, string>>>>({});
  const [loaded, setLoaded] = useState(false);
  const [dynamicMilestones, setDynamicMilestones] = useState<{ year: string; sectionKey: string }[]>([]);

  useEffect(() => {
    supabase.from('about_content').select('*').then(({ data }) => {
      if (data) {
        const map: Record<string, Record<string, Record<string, string>>> = {};
        const ms: { year: string; sectionKey: string }[] = [];
        (data as AboutSection[]).forEach((row) => {
          map[row.section_key] = row.content_i18n;
          if (row.section_key.startsWith('milestone_') && row.section_key !== 'milestones_header') {
            ms.push({ year: row.section_key.replace('milestone_', ''), sectionKey: row.section_key });
          }
        });
        ms.sort((a, b) => parseInt(a.year) - parseInt(b.year));
        setAboutData(map);
        setDynamicMilestones(ms);
      }
      setLoaded(true);
    });
  }, []);

  useEffect(() => {
    if (!loaded) return;
    const timer = setTimeout(() => {
      document.querySelectorAll('.reveal, .reveal-left, .reveal-right').forEach((el) => {
        el.classList.add('visible');
      });
    }, 100);
    return () => clearTimeout(timer);
  }, [loaded]);

  const g = (sectionKey: string, field: string): string => {
    const lang = i18n.language as Lang;
    const section = aboutData[sectionKey];
    if (section) {
      return section[lang]?.[field] || section['ko']?.[field] || '';
    }
    return '';
  };

  const values = [
    { icon: 'ri-handshake-line', sectionKey: 'value_partnership', titleFb: 'aboutPage.value.partnership.title', descFb: 'aboutPage.value.partnership.desc' },
    { icon: 'ri-shield-check-line', sectionKey: 'value_integrity', titleFb: 'aboutPage.value.integrity.title', descFb: 'aboutPage.value.integrity.desc' },
    { icon: 'ri-lightbulb-line', sectionKey: 'value_innovation', titleFb: 'aboutPage.value.innovation.title', descFb: 'aboutPage.value.innovation.desc' },
    { icon: 'ri-global-line', sectionKey: 'value_local', titleFb: 'aboutPage.value.local.title', descFb: 'aboutPage.value.local.desc' },
  ];

  const milestones = loaded
    ? dynamicMilestones
    : [
        { year: '2010', sectionKey: 'milestone_2010' },
        { year: '2013', sectionKey: 'milestone_2013' },
        { year: '2016', sectionKey: 'milestone_2016' },
        { year: '2019', sectionKey: 'milestone_2019' },
        { year: '2022', sectionKey: 'milestone_2022' },
        { year: '2024', sectionKey: 'milestone_2024' },
      ];

  return (
    <Layout>
      <title>{t('page.title.about')}</title>
      <meta name="description" content="14 years bridging Korea and Vietnam. KV Trade Alliance — 85+ specialists, $4.2B annual trade facilitated, offices in Seoul & Ho Chi Minh City. Meet our team." />
      <meta name="keywords" content="KV Trade Alliance about, Korea Vietnam trade agency history, bilateral trade experts, Seoul HCMC trade specialists" />

      {/* Hero Banner */}
      <section className="relative pt-28 pb-16 lg:pt-36 lg:pb-24 bg-navy overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/f07e34980e5048198c07ef59731b397c.jpg"
            alt="About KV Trade Alliance"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy/90 to-navy/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            {!loaded ? (
              <div className="space-y-4 animate-pulse">
                <div className="h-3 w-20 bg-white/20 rounded" />
                <div className="h-10 w-64 bg-white/20 rounded" />
                <div className="h-4 w-96 bg-white/20 rounded" />
                <div className="h-4 w-72 bg-white/20 rounded" />
              </div>
            ) : (
              <>
                <p className="text-xs font-sans text-silver/50 tracking-[0.2em] uppercase mb-4">
                  {g('hero', 'eyebrow')}
                </p>
                <h1 className="font-serif text-white text-3xl sm:text-4xl lg:text-6xl font-bold leading-tight mb-4 lg:mb-6">
                  <span className="not-italic font-bold text-white">
                    {g('hero', 'heading')}
                  </span>
                </h1>
                <p className="text-white/60 font-sans text-base lg:text-lg leading-relaxed max-w-xl">
                  {g('hero', 'desc')}
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section ref={ref} className="py-16 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          {!loaded ? (
            <div className="animate-pulse space-y-6 mb-16 lg:mb-24">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20">
                <div className="space-y-4">
                  <div className="h-3 w-16 bg-navy/10 rounded" />
                  <div className="h-8 w-72 bg-navy/10 rounded" />
                  <div className="h-4 w-full bg-navy/10 rounded" />
                  <div className="h-4 w-5/6 bg-navy/10 rounded" />
                  <div className="h-4 w-full bg-navy/10 rounded" />
                  <div className="h-4 w-4/6 bg-navy/10 rounded" />
                  <div className="h-10 w-32 bg-navy/10 rounded mt-4" />
                </div>
                <div className="rounded-2xl h-64 lg:h-[420px] bg-navy/10" />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-center mb-16 lg:mb-24">
              <div className="reveal-left">
                <p className="text-xs font-sans text-navy/40 tracking-[0.2em] uppercase mb-4">
                  {g('story', 'eyebrow')}
                </p>
                <h2 className="font-serif text-navy text-2xl lg:text-4xl font-bold mb-6 leading-tight">
                  {g('story', 'heading')}
                </h2>
                <p className="text-navy/60 text-sm lg:text-base font-sans leading-relaxed mb-4">
                  {g('story', 'p1')}
                </p>
                <p className="text-navy/60 text-sm lg:text-base font-sans leading-relaxed mb-4">
                  {g('story', 'p2')}
                </p>
                <p className="text-navy/60 text-sm lg:text-base font-sans leading-relaxed mb-8">
                  {g('story', 'p3')}
                </p>
                <Link to="/contact" className="inline-flex items-center gap-2 bg-navy text-white text-sm font-sans font-medium px-7 py-3.5 rounded hover:bg-navy-light transition-colors duration-200 cursor-pointer whitespace-nowrap">
                  {g('story', 'cta')} <i className="ri-arrow-right-line" />
                </Link>
              </div>
              <div className="reveal-right">
                <div className="rounded-2xl overflow-hidden h-64 lg:h-[420px]">
                  <img
                    src="https://readdy.ai/api/search-image?query=modern%20glass%20corporate%20headquarters%20office%20building%20Seoul%20South%20Korea%20Gangnam%20district%20business%20district%20daytime%20professional%20architecture%20exterior%20sleek%20design%20navy%20silver%20tones%20clean%20minimalist%20corporate%20building&width=700&height=500&seq=about_office&orientation=landscape"
                    alt="KV Trade Alliance Headquarters"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Mission & Values */}
          {!loaded ? (
            <div className="animate-pulse mb-16 lg:mb-24">
              <div className="text-center mb-10 space-y-3">
                <div className="h-3 w-20 bg-navy/10 rounded mx-auto" />
                <div className="h-8 w-48 bg-navy/10 rounded mx-auto" />
                <div className="h-4 w-36 bg-navy/10 rounded mx-auto" />
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                {[1,2,3,4].map(i => (
                  <div key={i} className="bg-[#F8F9FB] rounded-xl p-5 lg:p-7 space-y-3">
                    <div className="w-10 h-10 bg-navy/10 rounded-lg" />
                    <div className="h-5 w-24 bg-navy/10 rounded" />
                    <div className="h-3 w-full bg-navy/10 rounded" />
                    <div className="h-3 w-4/5 bg-navy/10 rounded" />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="reveal text-center mb-10 lg:mb-16">
                <p className="text-xs font-sans text-navy/40 tracking-[0.2em] uppercase mb-3">
                  {g('values_header', 'eyebrow')}
                </p>
                <h2 className="font-serif text-navy text-2xl lg:text-4xl font-bold mb-3">
                  {g('values_header', 'heading')}
                </h2>
                <p className="text-navy/50 font-sans text-sm lg:text-base italic">
                  {g('values_header', 'sub')}
                </p>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-16 lg:mb-24">
                {values.map((v, i) => (
                  <div key={v.sectionKey} className={`reveal stagger-${i + 1} bg-[#F8F9FB] rounded-xl p-5 lg:p-7 hover:bg-navy-50 transition-colors duration-300`}>
                    <div
                      className="w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center rounded-lg mb-4"
                      style={{ backgroundColor: '#0A1628' }}
                    >
                      <i
                        className={v.icon}
                        style={{ fontSize: '1.25rem', color: '#ffffff', lineHeight: 1, display: 'block' }}
                      />
                    </div>
                    <h3 className="font-serif text-navy text-base lg:text-lg font-semibold mb-2">
                      {g(v.sectionKey, 'title')}
                    </h3>
                    <p className="text-navy/55 text-xs lg:text-sm font-sans leading-relaxed">
                      {g(v.sectionKey, 'desc')}
                    </p>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Timeline */}
          {!loaded ? (
            <div className="animate-pulse">
              <div className="text-center mb-10 space-y-3">
                <div className="h-3 w-16 bg-navy/10 rounded mx-auto" />
                <div className="h-8 w-40 bg-navy/10 rounded mx-auto" />
              </div>
              <div className="space-y-8 lg:space-y-12">
                {[1,2,3,4].map(i => (
                  <div key={i} className="flex items-start gap-8">
                    <div className="flex-1 space-y-2 text-right pr-8">
                      <div className="h-7 w-16 bg-navy/10 rounded ml-auto" />
                      <div className="h-5 w-36 bg-navy/10 rounded ml-auto" />
                      <div className="h-3 w-full bg-navy/10 rounded" />
                    </div>
                    <div className="w-4 h-4 rounded-full bg-navy/10 mt-2 flex-shrink-0" />
                    <div className="flex-1" />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="reveal text-center mb-10 lg:mb-16">
                <p className="text-xs font-sans text-navy/40 tracking-[0.2em] uppercase mb-3">
                  {g('milestones_header', 'eyebrow')}
                </p>
                <h2 className="font-serif text-navy text-2xl lg:text-4xl font-bold">
                  {g('milestones_header', 'heading')}
                </h2>
              </div>
              <div className="relative">
                <div className="hidden lg:block absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-silver-light" />
                <div className="lg:hidden absolute left-2 top-0 bottom-0 w-px bg-silver-light" />
                <div className="space-y-8 lg:space-y-12">
                  {dynamicMilestones.map((m, i) => {
                    const title = g(m.sectionKey, 'title');
                    const desc = g(m.sectionKey, 'desc');
                    return (
                      <div key={m.sectionKey} className={`reveal stagger-${(i % 4) + 1} relative`}>
                        <div className="flex items-start gap-4 lg:hidden pl-8">
                          <div className="absolute left-0 top-1.5 w-5 h-5 flex items-center justify-center flex-shrink-0">
                            <div className="w-4 h-4 rounded-full bg-navy border-4 border-silver-light" />
                          </div>
                          <div>
                            <p className="font-serif text-navy text-2xl font-bold mb-0.5">{m.year}</p>
                            <h3 className="font-sans text-navy text-base font-semibold mb-1">{title}</h3>
                            <p className="text-navy/55 text-sm font-sans leading-relaxed">{desc}</p>
                          </div>
                        </div>
                        <div className={`hidden lg:flex items-start gap-8 ${i % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                          <div className={`flex-1 ${i % 2 === 0 ? 'text-right pr-8' : 'text-left pl-8'}`}>
                            <p className="font-serif text-navy text-3xl font-bold mb-1">{m.year}</p>
                            <h3 className="font-sans text-navy text-lg font-semibold mb-2">{title}</h3>
                            <p className="text-navy/55 text-sm font-sans leading-relaxed max-w-sm ml-auto">{desc}</p>
                          </div>
                          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-2">
                            <div className="w-4 h-4 rounded-full bg-navy border-4 border-silver-light" />
                          </div>
                          <div className="flex-1" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}
